import type { ReactNode } from 'react';

/** Page-title scale, used with `level={1}`. Every heading shares one weight; only the size differs. */
export type PageHeadingSize = 'display' | 'page' | 'listing' | 'compact';
/** Section scale, used with `level={2}` or `3`. */
export type SectionHeadingSize = 'xl' | 'lg' | 'md' | 'sm';
export type HeadingSize = PageHeadingSize | SectionHeadingSize;

const SIZE_CLASS: Record<HeadingSize, string> = {
  // Homepage and landing hero: 36 → 48 px
  display: 'text-[36px] leading-tight tracking-tight lg:text-5xl',
  // Category, subcategory, all products, 404: 28 → 38 px
  page: 'text-[28px] leading-tight sm:text-[38px]',
  // Blog, compatibility, search results, blog article: 24 → 30 px
  listing: 'text-2xl leading-tight sm:text-3xl',
  // Product detail: 20 → 22 px
  compact: 'text-xl leading-snug sm:text-[22px]',
  // Homepage sections: 28 → 38 px
  xl: 'text-[28px] leading-tight sm:text-[38px]',
  // Category and landing sections: 22 → 28 px
  lg: 'text-[22px] leading-tight sm:text-[28px]',
  // Product detail sections, carousels: 20 → 24 px
  md: 'text-xl leading-tight sm:text-2xl',
  // Search results, product listing sections, article body: 20 px
  sm: 'text-xl leading-tight',
};

/**
 * The one heading of the catalog. `level` keeps the document outline, `size` picks the look, so a sidebar
 * `h2` can still be small and a hero `h1` big. Spacing goes in `className` (Tailwind orders `mb-*`/`mt-*`
 * after `m-0`, so they win).
 */
export function Heading({
  level,
  size,
  id,
  className = '',
  children,
}: {
  level: 1 | 2 | 3;
  size: HeadingSize;
  id?: string;
  className?: string;
  children: ReactNode;
}) {
  const Element = `h${level}` as const;

  return (
    <Element id={id} className={`m-0 font-semibold ${SIZE_CLASS[size]} text-brand-ink ${className}`}>
      {children}
    </Element>
  );
}
